import * as React from 'react';
import {
  Text,
  TextArea,
  TerritorySelect,
  DateTime,
  Checkbox,
} from '../components/forms';
import Page from '../components/Page';
import Section from '../components/Section';
import Box from '../components/Box';
import { ButtonStyle } from '../components/Button';
import { ActionBar, ActionBarStyle } from '../components/ActionBar';
import { FeedItem, FeedEntryType, FeedResource, Feed } from '../feed';
import FeedEntry from '../components/FeedEntry';
import { v4 } from 'uuid';
import { ErrorFlash, SuccessFlash } from '../components/Flash';
import { ProfileContext, ProfileManager } from '../profile';
import { useRequestAsync, getHeaders } from '../api/client';

type Props = {
  feed: Feed;
};

function arrMove(arr: any[], fromIndex: number, toIndex: number) {
  const newArr: any[] = arr.slice();
  const element: any = newArr[fromIndex];
  newArr.splice(fromIndex, 1);
  newArr.splice(toIndex, 0, element);

  return newArr;
}

const update = (context: ProfileManager) =>
  useRequestAsync((data: Feed) => ({
    url: data.id ? `/feeds/${data.id}` : '/feeds/new',
    method: data.id ? 'PUT' : 'POST',
    body: JSON.stringify(data),
    headers: getHeaders(context),
  }));

const setValue = (obj: Feed, onChange: Function) => (
  field: string,
  newValue: any,
) =>
  onChange(
    Object.assign({}, obj, {
      [field]: newValue,
    }),
  );

export default function EditFeed(props: Props) {
  const context = React.useContext(ProfileContext);
  const [feed, setFeed] = React.useState(props.feed);

  const [state, submit] = update(context);

  const handleFormSubmit = () =>
    submit({
      ...props.feed.data,
      ...feed,
    });

  const addItem = () =>
    setFeed(
      Object.assign({}, feed, {
        data: feed.data.concat([
          {
            id: v4(),
            name: '',
            type: FeedEntryType.Feature,
            resource_type: FeedResource.Guide,
            resource_id: '',
          },
        ]),
      }),
    );

  const setFeedEntry = (index: number, item: FeedItem) => {
    const newData = feed.data.slice();

    newData[index] = item;

    setFeed(
      Object.assign({}, feed, {
        data: newData,
      }),
    );
  };
  const deleteFeedEntry = (index: number, item: FeedItem) => {
    const newData = feed.data.filter((_, itemIndex) => itemIndex !== index);

    setFeed(
      Object.assign({}, feed, {
        data: newData,
      }),
    );
  };

  const moveEntryDown = (index: number, item: FeedItem) => {
    const newIndex: number = index + 1;

    if (newIndex >= 0 && newIndex < feed.data.length) {
      setFeed(
        Object.assign({}, feed, {
          data: arrMove(feed.data, index, index + 1),
        }),
      );
    }
  };

  const moveEntryUp = (index: number, item: FeedItem) => {
    const newIndex: number = index - 1;

    if (newIndex >= 0 && newIndex < feed.data.length) {
      setFeed(
        Object.assign({}, feed, {
          data: arrMove(feed.data, index, index - 1),
        }),
      );
    }
  };

  return (
    <Page title={feed.name || 'A new feed'} subtitle="Edit feed">
      {state.error && <ErrorFlash error={state.error} />}
      {state.complete && state.data && (
        <SuccessFlash text="Feed has been saved" />
      )}
      <form onSubmit={handleFormSubmit} id="feed">
        <Section title="Basic details">
          <div className="flex">
            <div className="flex-auto w-50 pr2">
              <Text
                label="Name"
                field={['name']}
                id="name"
                defaultValue={feed.name}
                onValueChange={setValue(feed, setFeed)}
              />
              <TextArea
                label="Description"
                field={['description_text']}
                id="description_text"
                defaultValue={feed.description_text}
                onValueChange={setValue(feed, setFeed)}
              />
            </div>
            <div className="flex-auto w-50 pl2">
              <Checkbox
                label="Active?"
                field={['active']}
                id="active"
                defaultValue={feed.active}
                onValueChange={setValue(feed, setFeed)}
                tooltip="Display in the 'Discover' section"
              />
            </div>
          </div>
        </Section>
        <Section title="Visibility">
          <div className="flex">
            <div className="flex-auto w-50 pr2">
              <DateTime
                label="Visible from"
                field={['visible_from']}
                id="visible_from"
                defaultValue={feed.visible_from}
                onValueChange={setValue(feed, setFeed)}
              />
            </div>
            <div className="flex-auto w-50 pl2">
              <Checkbox
                label="Visible Worldwide?"
                field={['visible_worldwide']}
                id="visible_worldwide"
                defaultValue={feed.visible_worldwide}
                onValueChange={setValue(feed, setFeed)}
                tooltip="Display this feed to all users"
              />
              <TerritorySelect
                label="Visible in"
                field={['visible_in']}
                id="visible_in"
                defaultValue={feed.visible_in}
                onValueChange={setValue(feed, setFeed)}
              />
            </div>
          </div>
        </Section>
        <Section title="Entries">
          {feed.data.map((entry: FeedItem, index: number) => (
            <FeedEntry
              index={index}
              key={`${index}-${JSON.stringify(entry)}`}
              entry={entry}
              onChange={setFeedEntry}
              onMoveDown={moveEntryDown}
              onMoveUp={moveEntryUp}
              onDelete={deleteFeedEntry}
            />
          ))}
          <div className="flex justify-start white-30 b pv4">
            <button
              type="button"
              onClick={addItem}
              className="gold db link bg-transparent bw0 dim"
            >
              + Add item
            </button>
          </div>
        </Section>
        <ActionBar
          className={ActionBarStyle.alignCenter}
          actions={[
            {
              className: ButtonStyle.Confirm,
              label: 'Save',
              handler: handleFormSubmit,
            },
          ]}
        />
      </form>
    </Page>
  );
}
